import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import {
  Cart as CartConfig,
  useLocalStorage,
  UserLocale,
} from 'hooks/userPreferences';
import type {
  CartLineUpdateInput,
  UpdateCartItemMutation,
} from 'services/shopify';
import { ShopifyService } from 'services/shopify';
import type { Cart } from 'types/cart/Cart';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

import { CART_KEY } from './useCart';

export async function updateItem(
  queryClient: QueryClient,
  lineItem: CartLineUpdateInput,
  country: string,
  getCartId: () => { id?: string } | null,
  setCartId: (cartId: { id: string }) => void,
): Promise<NonNullable<UpdateCartItemMutation['cartLinesUpdate']>['cart']> {
  const { id: cartId } = getCartId() || {};
  const currentCart = queryClient.getQueryData<Cart>([CART_KEY, country]);

  if (cartId && currentCart) {
    try {
      const response = await ShopifyService.updateCartItem({
        cartId,
        lines: [lineItem],
      });

      if (response.cartLinesUpdate?.cart) {
        setCartId({ id: response.cartLinesUpdate.cart.id });
        return response.cartLinesUpdate.cart;
      }
    } catch (error) {
      console.error(error);
      throw new Error('Error updating item');
    }
  }
}

export const useUpdateItem = (queryClient: QueryClient) => {
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const { getValue, setStoredValue: setCartId } = useLocalStorage(CartConfig);

  return useMutation({
    mutationFn: (lineItem: CartLineUpdateInput) =>
      updateItem(queryClient, lineItem, countryCode, getValue, setCartId),
    onSuccess: (cart) => {
      if (cart) {
        const formattedCart = mapShopifyCart(cart);
        queryClient.setQueriesData(
          { queryKey: [CART_KEY, countryCode] },
          formattedCart,
        );
      }
    },
  });
};
